import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import AutomationListHeader from "./list/AutomationListHeader";
import AutomationListStats from "./list/AutomationListStats";
import AutomationListFilters from "./list/AutomationListFilters";
import AutomationListTable from "./list/AutomationListTable";
import ConfirmationModal from "../../../ers/common/components/ConfirmationModal";
import { notify } from "../../../../services/toast.config";
import {
  selectActiveCompany,
  selectIsLoading,
  selectCompanyOptions,
} from "../../../../core/data/redux/automations/selector";
import {
  setActiveCompany,
  resetActiveCompany,
} from "../../../../core/data/redux/automations/slice";
import {
  fetchCompanies,
  fetchAllWorkflows,
  deleteWorkflow,
  toggleWorkflowStatus,
  runWorkflowManually,
} from "../../../../core/data/redux/automations/thunks";
import { all_routes } from "../../../router/all_routes";
import { WORKFLOW_TRIGGER_TYPE } from "../../workflow/utils/enums";
import { WORKFLOW_LIST_FETCH_LIMIT } from "../constants/automation.constants";

import type { AppDispatch } from "../../../../core/data/redux/store";
import type {
  CompanyOption,
  WorkflowData,
  WorkflowStats,
} from "../types/automation.types";

interface AutomationListProps {
  workflowCategory: string;
  title: string;
  breadcrumb: string;
  module: string;
}

const AutomationList: React.FC<AutomationListProps> = ({
  workflowCategory,
  title,
  breadcrumb,
  module,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const routes = {
    dashboard: all_routes.adminDashboard,
    create: `/automation/${module}/create`,
    edit: `/automation/${module}/edit/:id`,
    history: `/automation/${module}/history/:id`,
    backRoute: `/automation/${module}`,
  };

  const activeCompany = useSelector(selectActiveCompany);
  const isLoading = useSelector(selectIsLoading);
  const companyOptions = useSelector(selectCompanyOptions);

  const [workflows, setWorkflows] = useState<WorkflowData[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteWorkflowId, setDeleteWorkflowId] = useState<number | null>(null);

  const activeCompanyKey = activeCompany?.company_id
    ? `${activeCompany.company_id}|${activeCompany.company_type}`
    : null;

  const activeFiltersCount = [statusFilter, startDate || endDate].filter(
    Boolean,
  ).length;

  const filteredWorkflows = useMemo(() => {
    let filtered = [...workflows];

    if (statusFilter) {
      filtered = filtered.filter((workflow) =>
        statusFilter === "Active" ? workflow.is_active : !workflow.is_active,
      );
    }

    if (startDate && endDate) {
      filtered = filtered.filter((workflow) => {
        const createdDay = dayjs(workflow.created_at).format("YYYY-MM-DD");
        return createdDay >= startDate && createdDay <= endDate;
      });
    }

    return filtered;
  }, [workflows, statusFilter, startDate, endDate]);

  const stats: WorkflowStats = useMemo(
    () => ({
      total: workflows.length,
      active: workflows.filter((w) => w.is_active).length,
      inactive: workflows.filter((w) => !w.is_active).length,
      manual: workflows.filter(
        (w) => w.execution_type === WORKFLOW_TRIGGER_TYPE.MANUAL,
      ).length,
    }),
    [workflows],
  );

  const fetchWorkflows = useCallback(async () => {
    if (!activeCompany?.company_id) return;

    try {
      const result = await dispatch(
        fetchAllWorkflows({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          params: {
            page: 1,
            limit: WORKFLOW_LIST_FETCH_LIMIT,
            workflow_type: workflowCategory,
          },
        }),
      ).unwrap();

      setWorkflows(result?.data?.workflows || []);
    } catch (error: any) {
      notify.error(error?.message || "Failed to load workflows");
      setWorkflows([]);
    }
  }, [
    dispatch,
    activeCompany?.company_id,
    activeCompany?.company_type,
    workflowCategory,
  ]);

  useEffect(() => {
    const initializeCompanies = async () => {
      try {
        dispatch(resetActiveCompany());
        await dispatch(fetchCompanies()).unwrap();
      } catch (error: any) {
        notify.error(error?.message || "Failed to load companies");
      }
    };

    initializeCompanies();
  }, [dispatch]);

  useEffect(() => {
    if (activeCompany?.company_id) {
      fetchWorkflows();
    }
  }, [activeCompany?.company_id, activeCompany?.company_type, fetchWorkflows]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, startDate, endDate]);

  const handleCompanyChange = useCallback(
    (company: CompanyOption) => {
      dispatch(
        setActiveCompany({
          company_id: company.companyId,
          company_name: company.companyName,
          company_type: company.companyType,
        }),
      );
      setStatusFilter(null);
      setStartDate(null);
      setEndDate(null);
      setPage(1);
    },
    [dispatch],
  );

  const handleDateChange = useCallback(
    (start: string | null, end: string | null) => {
      setStartDate(start);
      setEndDate(end);
    },
    [],
  );

  const handleReset = useCallback(() => {
    setStatusFilter(null);
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  }, []);

  const handleEdit = useCallback(
    (record: WorkflowData) => {
      navigate(routes.edit.replace(":id", record.id.toString()), {
        state: { workflowCategory, module, title },
      });
    },
    [navigate, routes.edit, workflowCategory, module, title],
  );

  const handleHistory = useCallback(
    (record: WorkflowData) => {
      navigate(routes.history.replace(":id", record.id.toString()), {
        state: {
          title: "Automation Execution History",
          breadcrumb: "Execution History",
          backRoute: routes.backRoute,
          dashboardRoute: routes.dashboard,
          callingBreadcrumb: breadcrumb,
        },
      });
    },
    [navigate, routes.history, routes.backRoute, routes.dashboard, breadcrumb],
  );

  const handleDeleteModalOpen = useCallback((workflowId: number) => {
    setDeleteWorkflowId(workflowId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteWorkflowId(null);
  }, []);

  const handleDelete = async () => {
    if (!deleteWorkflowId) return;

    try {
      await dispatch(
        deleteWorkflow({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          workflowId: deleteWorkflowId,
        }),
      ).unwrap();

      notify.success("Workflow deleted successfully");
      handleCloseModal();
      await fetchWorkflows();
    } catch (error: any) {
      notify.error(error?.message || "Failed to delete workflow");
    }
  };

  const handleToggleStatus = async (
    workflowId: number,
    currentStatus: boolean,
  ) => {
    try {
      await dispatch(
        toggleWorkflowStatus({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          workflowId,
          isActive: !currentStatus,
        }),
      ).unwrap();

      notify.success(
        `Workflow ${!currentStatus ? "activated" : "deactivated"} successfully`,
      );
      await fetchWorkflows();
    } catch (error: any) {
      notify.error(error?.message || "Failed to toggle workflow status");
    }
  };

  const handleRunManually = async (workflowId: number) => {
    try {
      await dispatch(
        runWorkflowManually({
          companyId: activeCompany.company_id.toString(),
          companyType: activeCompany.company_type,
          workflowId,
        }),
      ).unwrap();

      notify.success(
        "Workflow execution started successfully, show logs for details",
      );
    } catch (error: any) {
      notify.error(error?.message || "Failed to run workflow!");
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <AutomationListHeader
            title={title}
            breadcrumb={breadcrumb}
            workflowCategory={workflowCategory}
            dashboardRoute={routes.dashboard}
            createRoute={routes.create}
            companies={companyOptions}
            activeCompanyKey={activeCompanyKey}
            companiesLoading={isLoading && companyOptions.length === 0}
            onCompanyChange={handleCompanyChange}
          />

          <AutomationListStats
            stats={stats}
            loading={isLoading && workflows.length === 0}
          />

          <AutomationListFilters
            statusFilter={statusFilter}
            startDate={startDate}
            endDate={endDate}
            activeFiltersCount={activeFiltersCount}
            onStatusChange={setStatusFilter}
            onDateChange={handleDateChange}
            onReset={handleReset}
          />

          <AutomationListTable
            workflows={filteredWorkflows}
            loading={isLoading}
            page={page}
            limit={limit}
            total={filteredWorkflows.length}
            onPageChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            onEdit={handleEdit}
            onHistory={handleHistory}
            onDelete={handleDeleteModalOpen}
            onToggleStatus={handleToggleStatus}
            onRunManually={handleRunManually}
          />
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        message="Are you sure you want to delete this workflow? This action cannot be undone."
        title="Delete Workflow"
        onConfirm={handleDelete}
        type="danger"
      />
    </>
  );
};

export default AutomationList;
